import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { ticketUpdate } from '~/actions/ticket';
import { deleteTicket, partialUpdateTicket } from '~/api/tickets';
import { User } from '~/components/Shared/User/User';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { usePanelContext } from '~/eds/src/components/Panel/PanelContext';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { TicketNegotiationStatus } from '~/enums/TicketNegotiationStatus';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import {
  AddToGroupPanel,
  AddToGroupPanelId,
} from '~/features/groups/AddToGroupButton/AddToGroupPanel';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCanEditEntityGroups } from '~/hooks/useCanEditEntityGroups';
import { api } from '~/redux/api/createApi';
import { TAG_BY_ACTIVITY_LOG } from '~/redux/api/endpoints/tickets';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Steps from '~/ui/components/Steps/Steps';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';
import { getSimplifiedStageNames, getStagePermissions } from '~/utils/ticket';

import { TICKET_NAME_MAX_LENGTH } from './constants';

function TicketHeader({
  ticket,
  loading,
  // connected
  canDelete,
  canCancel,
  isCompleted,
  ticketUpdate,
}) {
  const [inputTicketName, setInputTicketName] = useState(ticket.name);
  const [cancelNote, setCancelNote] = useState();
  const [cancelTicket] = api.endpoints.cancelTicket.useMutation();
  const { location, navigate } = useRouting();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { hasPermission: hasEditEntityGroupsPermission } =
    useCanEditEntityGroups();
  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const canAddToGroup =
    hasEditEntityGroupsPermission && hasGroupedTicketAndDocs;
  const { openPanel } = usePanelContext();

  useEffect(() => {
    setInputTicketName(ticket.name);
  }, [ticket.name]);

  const [deleteModal, showDeleteModal] = useModal({
    title: intl.formatMessage({
      id: 'TicketHeader.deleteTicketTitle',
      defaultMessage: 'Delete ticket?',
      description: 'Title for delete ticket modal',
    }),
    actionButton: {
      text: intl.formatMessage({
        id: 'TicketHeader.deleteButton',
        defaultMessage: 'Delete',
        description: 'Delete button text',
      }),
      variant: 'red',
      errorHandler: () =>
        toast({
          message: intl.formatMessage({
            id: 'TicketHeader.deleteErrorMessage',
            defaultMessage:
              'Unable to delete the ticket. Please refresh the page and try again.',
            description: 'Error message when ticket deletion fails',
          }),
          status: 'danger',
        }),
      promise: async () => {
        await deleteTicket({ ticketId: ticket.id }, true);
        toast({
          message: intl.formatMessage(
            {
              id: 'TicketHeader.deleteSuccessMessage',
              defaultMessage: 'Ticket "{ticketName}" successfully deleted.',
              description:
                'Success message when ticket is deleted. Example: ticketName = "Contract Review"',
            },
            { ticketName: ticket.name },
          ),
          status: 'success',
        });
        navigate('/workflow/tickets');
      },
    },
    content: (
      <FlexLayout flexDirection="column" space={8}>
        <FlexLayout flexDirection="column" space={2}>
          <Text variant="xs-dense">
            <FormattedMessage
              id="TicketHeader.deleteConfirmationText"
              defaultMessage="Are you sure you want to delete <strong>{ticketName}</strong> ticket?"
              description="Confirmation text for deleting a ticket. Example: ticketName = 'Document Name'"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                ticketName: ticket.name,
              }}
            />
          </Text>
          <Text variant="xs-dense-bold">
            {intl.formatMessage({
              id: 'TicketHeader.deleteWarningText',
              defaultMessage: "You won't be able to undo this action.",
              description: 'Warning text about irreversible deletion',
            })}
          </Text>
        </FlexLayout>
      </FlexLayout>
    ),
  });

  const [renameModal, showRenameModal] = useModal({
    title: intl.formatMessage({
      id: 'TicketHeader.renameTicketTitle',
      defaultMessage: 'Rename ticket',
      description: 'Title for rename ticket modal',
    }),
    onCancel: () => setInputTicketName(ticket.name),
    actionButton: {
      disabled:
        inputTicketName === ticket.name ||
        !inputTicketName.trim() ||
        inputTicketName.length > TICKET_NAME_MAX_LENGTH,
      text: intl.formatMessage({
        id: 'TicketHeader.renameButton',
        defaultMessage: 'Rename',
        description: 'Rename button text',
      }),
      variant: 'primary',
      errorHandler: () =>
        toast({
          message: intl.formatMessage({
            id: 'TicketHeader.renameErrorMessage',
            defaultMessage:
              'Unable to rename the ticket. Please refresh the page and try again.',
            description: 'Error message when ticket renaming fails',
          }),
          status: 'danger',
        }),
      promise: async () => {
        const res = await partialUpdateTicket(ticket.id, {
          name: inputTicketName,
        });
        ticketUpdate({ name: res.name });
        toast({
          message: intl.formatMessage({
            id: 'TicketHeader.renameSuccessMessage',
            defaultMessage: 'Ticket successfully renamed.',
            description: 'Success message when ticket is renamed',
          }),
          status: 'success',
        });
      },
    },
    content: (
      <FormField
        autoFocus
        label={intl.formatMessage({
          id: 'TicketHeader.nameLabel',
          defaultMessage: 'Name',
          description: 'Label for ticket name input',
        })}
        input={TextInput}
        value={inputTicketName}
        onChange={setInputTicketName}
        error={
          inputTicketName.length > TICKET_NAME_MAX_LENGTH
            ? intl.formatMessage(
                {
                  id: 'TicketHeader.nameOverMaxLengthError',
                  defaultMessage:
                    '{count}/{maxLength} (Name cannot be longer than {maxLength} characters)',
                  description:
                    'Error message below input when renaming a ticket and the length of the name is over the limit. Example: count = 1025',
                },
                {
                  count: inputTicketName?.length ?? 0,
                  maxLength: TICKET_NAME_MAX_LENGTH,
                },
              )
            : undefined
        }
      />
    ),
  });

  const onCloseCancelModal = () => setCancelNote(null);

  const [cancelModal, showCancelModal] = useModal({
    title: intl.formatMessage({
      id: 'TicketHeader.cancelTicketTitle',
      defaultMessage: 'Cancel this ticket?',
      description: 'Title for cancel ticket modal',
    }),
    onCancel: onCloseCancelModal,
    onHide: onCloseCancelModal,
    actionButton: {
      text: intl.formatMessage({
        id: 'TicketHeader.cancelTicketButton',
        defaultMessage: 'Cancel Ticket',
        description: 'Cancel ticket button text',
      }),
      errorHandler: () => {
        toast({
          message: intl.formatMessage({
            id: 'TicketHeader.cancelErrorMessage',
            defaultMessage:
              'Unable to cancel ticket. Please refresh the page and try again.',
            description: 'Error message when ticket cancellation fails',
          }),
          status: 'danger',
        });
      },
      promise: async () => {
        const res = await cancelTicket({
          ticketId: ticket.id,
          note: cancelNote,
        }).unwrap();
        dispatch(api.util.invalidateTags([TAG_BY_ACTIVITY_LOG]));
        ticketUpdate({ isCancelled: true, cancelledDate: res.cancelledDate });
        const esigErrorMessage = res?.esigErrorMessage;
        esigErrorMessage
          ? toast({
              message: esigErrorMessage,
              status: 'info',
            })
          : toast({
              message: intl.formatMessage({
                id: 'TicketHeader.cancelSuccessMessage',
                defaultMessage: 'Ticket successfully canceled.',
                description: 'Success message when ticket is canceled',
              }),
              status: 'success',
            });
      },
    },
    content: (
      <Layout direction="column" spacing={8}>
        <Text variant="xs-dense">
          <FormattedMessage
            id="TicketHeader.cancelConfirmationText"
            defaultMessage="Are you sure you want to cancel this workflow ticket? <bold>You won't be able to restore this ticket after canceling it.</bold>"
            description="Confirmation text for canceling a ticket"
            values={{
              bold: (chunks) => <Text isBold>{chunks}</Text>,
            }}
          />
        </Text>

        <FormField
          label={intl.formatMessage({
            id: 'TicketHeader.noteLabel',
            defaultMessage: 'Note',
            description: 'Label for note input',
          })}
          description={intl.formatMessage({
            id: 'TicketHeader.noteDescription',
            defaultMessage: 'Leave a comment outlining the reason.',
            description: 'Description for note input',
          })}
          name="notes"
          value={cancelNote}
          onChange={setCancelNote}
          placeholder={intl.formatMessage({
            id: 'TicketHeader.notePlaceholder',
            defaultMessage: 'Note',
            description: 'Placeholder for note input',
          })}
          autoFocus
          input={TextArea}
        />
      </Layout>
    ),
  });

  const navigateBack = () => {
    /** We needed to indicate that ticket page was accessed via a redirection from
     * another page (e.g. Dashboards). In this case, the back button should return
     * to the origin of the redirection.
     */
    dispatch(slices.turnTracking.actions.reset());
    location.state && location.state.isRedirect
      ? navigate(-1)
      : navigate('/workflow/tickets');
  };

  const getMenuItems = () => {
    const items = [];

    items.push({
      id: 'rename-ticket',
      label: intl.formatMessage({
        id: 'TicketHeader.renameTicketMenuItem',
        defaultMessage: 'Rename ticket',
        description: 'Menu item for renaming ticket',
      }),
      onClick: () => showRenameModal(),
    });

    if (!isCompleted && canDelete) {
      const isSentToSupplierPortal =
        ticket.negotiationStatus === TicketNegotiationStatus.InProgress;
      items.push({
        id: 'delete-ticket',
        label: intl.formatMessage({
          id: 'TicketHeader.deleteTicketMenuItem',
          defaultMessage: 'Delete ticket',
          description: 'Menu item for deleting ticket',
        }),
        onClick: () => showDeleteModal(),
        disabled: isSentToSupplierPortal,
        disabledReason: isSentToSupplierPortal
          ? intl.formatMessage({
              id: 'TicketHeader.deleteTicketDisabledReason',
              defaultMessage:
                'Ticket cannot be deleted because it has been sent to the Supplier Portal.',
              description:
                'Reason why delete ticket is disabled when ticket was sent to supplier portal',
            })
          : undefined,
      });
    }
    if (!isCompleted && canCancel) {
      items.push({
        id: 'cancel-ticket',
        label: intl.formatMessage({
          id: 'TicketHeader.cancelTicketMenuItem',
          defaultMessage: 'Cancel ticket',
          description: 'Menu item for canceling ticket',
        }),
        onClick: () => showCancelModal(),
        disabled: ticket.isCancelled,
      });
    }

    if (canAddToGroup) {
      items.push({
        id: 'add-to-group',
        label: intl.formatMessage({
          id: 'TicketHeader.addToGroupMenuItem',
          defaultMessage: 'Add to Group',
          description: 'Menu item for adding ticket to group',
        }),
        onClick: () => openPanel(AddToGroupPanelId),
      });
    }

    return [{ options: items, label: '' }];
  };

  const steps = getSimplifiedStageNames(ticket, intl);

  return (
    <>
      <div>
        <Layout
          align="center"
          styles={{
            paddingBottom: '24px',
          }}
        >
          <IconButton
            icon="arrow-back"
            onClick={navigateBack}
            tooltip={intl.formatMessage({
              id: 'TicketHeader.backToAllTicketsTooltip',
              defaultMessage: 'Back to all tickets',
              description: 'Tooltip for back button',
            })}
          />
          <FlexLayout flexGrow="1">
            <FlexLayout flexDirection="column">
              <ContentContainer loadingContent={{ isLoading: loading }}>
                <Text
                  data-testid="ticket-name"
                  as="h2"
                  color="gray-900"
                  variant="2xl-spaced-bold"
                  sx={{
                    wordBreak: 'break-word',
                  }}
                >
                  {ticket.name}
                </Text>
                <span style={{ height: '8px' }} />
                <Text color="gray-600" variant="xs-dense">
                  <FormattedMessage
                    id="TicketHeader.submittedByText"
                    defaultMessage="Submitted by <bold>{user}</bold> on {date}"
                    description="Text showing who submitted the ticket and when. Example: user = 'John Smith', date = '2024-01-15'"
                    values={{
                      bold: (chunks) => (
                        <Text color="gray-700" variant="xs-dense-bold">
                          {chunks}
                        </Text>
                      ),
                      user: <User id={ticket.creatorId} />,
                      date: (
                        <DateText
                          date={
                            ticket.createdDate
                              ? new Date(ticket.createdDate)
                              : undefined
                          }
                          format="short_datetime"
                          enableTick
                        />
                      ),
                    }}
                  />
                </Text>
              </ContentContainer>
            </FlexLayout>
          </FlexLayout>
          {!loading && (
            <FlexLayout alignItems="center" flex="none" spacing="x4">
              <Steps
                currentStepValue={ticket.stage}
                steps={steps}
                isCompleted={isCompleted}
                isCancelled={ticket.isCancelled}
              />
              <ActionsMenu
                id={`workflow_ticket_header_id=${ticket.id}`}
                actions={getMenuItems()}
                name={intl.formatMessage({
                  id: 'TicketHeader.ticketActionsMenuName',
                  defaultMessage: 'Ticket Actions',
                  description: 'Name for ticket actions menu',
                })}
              />
            </FlexLayout>
          )}
        </Layout>
        {deleteModal}
        {renameModal}
        {cancelModal}
        {canAddToGroup && (
          <AddToGroupPanel
            preselectedItems={[
              {
                resourceId: String(ticket.id ?? ''),
                resourceType: 'ticket',
              },
            ]}
          />
        )}
      </div>
    </>
  );
}

const mapStateToProps = ({ ticket }) => ({
  isCompleted: ticket.status === TicketStatusType.Completed,
  canDelete: getStagePermissions(ticket.permissionMap, ticket.stage).includes(
    TicketPermissionType.Delete,
  ),
  canCancel:
    getStagePermissions(ticket.permissionMap, ticket.stage).includes(
      TicketPermissionType.Cancel,
    ) && !ticket.wssContractId,
});

export default connect(mapStateToProps, {
  ticketUpdate,
})(TicketHeader);
